import { FRAME_MS, MOTION_FRAMES, type ColourRoleName } from "odyssey-shared";
import { barFill } from "./bar.ts";

/**
 * How a health bar reads (ADR-061's amendment "a health bar shows any health, and flashes when low", the author's
 * ruling of 2026-09-27), for every bar that draws health: the world bars under nameplates and the vitals frame's
 * row alike. Any health above 0 draws at least one fill pixel, so a living character never shows an empty bar; below
 * `LOW_HEALTH_PERCENT` of the maximum the fill flashes on the motion table's `blink`, lit and dark on the same frames
 * of the clock for every bar, so a crowd flashes together; under reduced motion it stays lit in
 * `LOW_HEALTH_STEADY_ROLE` instead; and 0, the dead, draws empty. Progress bars keep the kit's plain crop (`barFill`),
 * where a sliver of progress earns no pixel.
 */

/**
 * The share of the maximum, in percent, below which health is low and its fill flashes; at exactly this share it
 * does not.
 */
export const LOW_HEALTH_PERCENT = 10;

/**
 * The colour role a low bar's fill is drawn in, steady, when the viewer asks for reduced motion: amber against the
 * ink track, as far from the health red as the palette's alarm colours go.
 */
export const LOW_HEALTH_STEADY_ROLE: ColourRoleName = "warning";

/**
 * The frames of the `blink` cycle the fill is lit for; it is dark for the rest.
 */
const LIT_FRAMES = MOTION_FRAMES.blink / 2;

/**
 * The width a health bar's fill is cropped to: the kit's crop (`barFill`), except that any health above 0 on a
 * track with an inside draws at least one logical pixel.
 *
 * @param health The health.
 * @param max The maximum health.
 * @param inner The width of the track's inside in logical pixels.
 * @returns The fill's width in whole logical pixels, from 0 to `inner`; 0 only for no health or no valid bar.
 * @throws RangeError when `inner` is not a whole number of logical pixels or is negative.
 * @example
 * healthFill(3, 100, 30);
 * // 1, where barFill gives 0
 */
export function healthFill(health: number, max: number, inner: number): number {
  const fill = barFill(health, max, inner);
  return fill === 0 && health > 0 && max > 0 && inner > 0 ? 1 : fill;
}

/**
 * Whether health is low: above 0 and below `LOW_HEALTH_PERCENT` of the maximum, compared without division so a
 * share of exactly 10 percent is never low by rounding.
 *
 * @param health The health.
 * @param max The maximum health.
 * @returns True when the fill flashes (or, under reduced motion, takes the steady colour).
 */
export function healthLow(health: number, max: number): boolean {
  return health > 0 && max > 0 && health * 100 < LOW_HEALTH_PERCENT * max;
}

/**
 * Whether a low bar's fill is lit at a moment of the clock: lit for the first half of each `blink` cycle (6 frames
 * of 40 ms) and dark for the second, about 2.1 flashes a second, under `FLASH_MAX_HZ`. Every bar read at the same
 * time gets the same answer, which keeps a crowd's bars in step. A time before the clock's start, or not a number,
 * reads lit.
 *
 * @param timeMs The clock's time in ms: the scene's clock on the canvas.
 * @returns True in the lit phase.
 */
export function lowHealthLit(timeMs: number): boolean {
  const frame = timeMs > 0 ? Math.floor(timeMs / FRAME_MS) : 0;
  return frame % MOTION_FRAMES.blink < LIT_FRAMES;
}
